<script>
import { Helper } from '@/mixin/helper'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import FormDeviceCamera from './FormDeviceCamera'
import * as deviceCameraApi from '@/services/rest-api-sms/lib/device-camera/device-camera'
import { toCreatePayload, toUpdatePayload } from './lib/device-camera'
import { getToolsClient } from '@/services/tools'

const toolsAxios = getToolsClient()

export default {
  name: 'DeviceCameraConfiguration',
  components: {
    FormDeviceCamera,
    Layout,
    PageHeader,
  },
  mixins: [Helper],
  data() {
    return {
      deviceCameras: [],
      filteredItems: [],

      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      totalPages: 0,

      filters: {
        deviceCode: null,
      },

      deviceTypeOptions: [],
      deviceTypeMap: {},

      fields: [
        { key: 'device_code', label: 'Device Type', sortable: true },
        { key: 'max_camera', label: 'Max Camera', sortable: true },
        { key: 'index_start', label: 'Index Start', sortable: true },
        { key: 'is_support_adas', label: 'Support ADAS', sortable: true },
        { key: 'is_support_dms', label: 'Support DMS', sortable: true },
        { key: 'created_at', label: 'Created At', sortable: true },
        { key: 'updated_at', label: 'Updated At', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],

      showFormModal: false,
      formData: null,

      isLoading: false,
      isFormLoading: false,
      isDeleting: false,

      selectedServerRegion: 'SG',
      showDeleteModal: false,
      deleteTarget: null,
    }
  },
  computed: {
    title() {
      return 'Konfigurasi Camera'
    },
  },
  mounted() {
    this.getDeviceTypes()
    this.loadDeviceCameras()
  },
  methods: {
    async getDeviceTypes() {
      try {
        const { data } = await toolsAxios.get('/qcdevice/devicetypes')
        this.deviceTypeOptions = data
          .map(({ device_code, device_name }) => {
            this.deviceTypeMap[device_code] = device_name
            return { id: device_code, name: device_name }
          })
      } catch (error) {
        this.$toast.error('Failed to load device types')
      }
    },
    async loadDeviceCameras() {
      this.isLoading = true
      try {
        await this.applyFilters()
      } catch (error) {
        this.$toast.error('Failed to load camera configurations')
        this.deviceCameras = []
        this.filteredItems = []
      } finally {
        this.isLoading = false
      }
    },
    async applyFilters() {
      let params = {
        limit: this.perPage,
        page: this.currentPage,
        sorts: ['updated_at desc']
      }
      if (this.filters.deviceCode !== null) {
        params = {
          ...params,
          filters: [`device_code eq ${this.filters.deviceCode}`]
        }
      }
      const response = await deviceCameraApi.get(params, this.selectedServerRegion)

      this.deviceCameras = response?.data?.paginated_result || []
      this.filteredItems = response?.data?.paginated_result || []
      this.totalRows = response?.metadata?.total_count || 0
      this.totalPages = response?.metadata?.total_page || 0
      this.currentPage = response?.metadata?.page || 1
    },
    changePage(page) {
      this.currentPage = page
      this.applyFilters()
    },
    changeLimit() {
      this.currentPage = 1
      this.applyFilters()
    },
    filterByDeviceCode() {
      this.currentPage = 1
      this.applyFilters()
    },
    onServerRegionChange() {
      this.currentPage = 1
      this.applyFilters()
    },
    resetFilters() {
      this.filters = {
        deviceCode: null,
      }
      this.currentPage = 1
      this.applyFilters()
    },
    openCreateModal() {
      this.formData = null
      this.showFormModal = true
    },
    openEditModal(item) {
      this.formData = { ...item }
      this.showFormModal = true
    },
    openDeleteModal(item) {
      this.deleteTarget = { ...item }
      this.showDeleteModal = true
    },
    cancelDelete() {
      this.showDeleteModal = false
      this.deleteTarget = null
    },
    async confirmDelete() {
      if (!this.deleteTarget?.device_code) return

      this.isDeleting = true
      try {
        await deviceCameraApi.remove(this.deleteTarget.device_code, this.selectedServerRegion)
        this.$toast.success('Camera configuration deleted successfully')
        this.showDeleteModal = false
        this.deleteTarget = null
        await this.applyFilters()
      } catch (error) {
        this.$toast.error(error?.message || 'Failed to delete camera configuration')
      } finally {
        this.isDeleting = false
      }
    },
    async handleFormSubmit(formData) {
      this.isFormLoading = true
      try {
        if (this.formData?.device_code) {
          await deviceCameraApi.update(
            this.formData.device_code,
            toUpdatePayload(formData),
            this.selectedServerRegion
          )
        } else {
          await deviceCameraApi.create(toCreatePayload(formData), this.selectedServerRegion)
        }

        await this.applyFilters()
        this.showFormModal = false
        this.$toast.success('Camera configuration saved successfully')
      } catch (error) {
        this.$toast.error(error?.message || 'Failed to save camera configuration')
      } finally {
        this.isFormLoading = false
      }
    },
    deviceTypeLabel(deviceCode) {
      return this.deviceTypeMap[deviceCode] || deviceCode || '-'
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <div class="card mb-3">
      <div class="card-body">
        <b-row class="mb-3">
          <b-col cols="12" md="3" class="mb-2">
            <label>Server Region</label>
            <b-form-select
              v-model="selectedServerRegion"
              class="form-select form-select-md __limit--dropdown"
              @change="onServerRegionChange"
            >
              <b-form-select-option value="SG">Singapura</b-form-select-option>
              <b-form-select-option value="ID">Indonesia</b-form-select-option>
            </b-form-select>
          </b-col>
          <b-col cols="12" md="4" class="mb-2">
            <label>Device Type</label>
            <v-select
              v-model="filters.deviceCode"
              :options="deviceTypeOptions"
              label="name"
              :reduce="(option) => option.id"
              placeholder="Select device code"
              @input="filterByDeviceCode"
            />
          </b-col>
          <b-col cols="12" md="5" class="mb-2 d-flex align-items-end justify-content-end">
            <b-button variant="outline-secondary" class="me-2" @click="resetFilters">
              Reset
            </b-button>
            <b-button variant="primary" @click="openCreateModal">
              <i class="mdi mdi-plus"></i> Create Configuration
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <b-table
          :items="deviceCameras"
          :fields="fields"
          :busy="isLoading"
          class="mb-3"
          responsive
          striped
          hover
          show-empty
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong class="ms-2">Loading...</strong>
            </div>
          </template>

          <template #empty>
            <div class="text-center">No camera configurations found</div>
          </template>

          <template #cell(device_code)="{ item }">
            {{ deviceTypeLabel(item.device_code) }}
          </template>

          <template #cell(is_support_adas)="{ item }">
            <b-badge :variant="item.is_support_adas ? 'success' : 'secondary'">
              {{ item.is_support_adas ? 'Yes' : 'No' }}
            </b-badge>
          </template>

          <template #cell(is_support_dms)="{ item }">
            <b-badge :variant="item.is_support_dms ? 'success' : 'secondary'">
              {{ item.is_support_dms ? 'Yes' : 'No' }}
            </b-badge>
          </template>

          <template #cell(created_at)="data">
            {{ data.item.created_at ? momentDMHms(data.value) : '-' }}
          </template>

          <template #cell(updated_at)="data">
            {{ data.item.updated_at ? momentDMHms(data.value) : '-' }}
          </template>

          <template #cell(actions)="{ item }">
            <b-button size="sm" variant="info" class="me-2" @click="openEditModal(item)">
              <i class="mdi mdi-pencil"></i>
            </b-button>
            <b-button size="sm" variant="danger" @click="openDeleteModal(item)">
              <i class="mdi mdi-delete"></i>
            </b-button>
          </template>
        </b-table>

        <div class="row">
          <div class="col-sm-12 col-md-6">
            <div id="device-camera-table-length" class="dataTables_length">
              <label class="d-inline-flex align-items-center">
                <span>Menampilkan</span>
                <span>
                  <b-form-select
                    v-model="perPage"
                    class="form-select form-select-sm"
                    size="sm"
                    :options="pageOptions"
                    @change="changeLimit"
                  />
                </span>
                <span>entri.&nbsp;&nbsp;</span>
                <span>
                  Menampilkan&nbsp;
                  <strong>
                    {{ filteredItems?.length > 0 ? (currentPage - 1) * perPage + 1 : 0 }}
                  </strong>
                  &nbsp;sampai&nbsp;
                  <strong>
                    {{
                      currentPage != totalPages
                        ? currentPage * perPage
                        : (currentPage - 1) * perPage + filteredItems?.length
                    }}
                  </strong>
                  &nbsp;dari&nbsp;
                  <strong>{{ totalRows }}</strong>
                  &nbsp;entri.
                </span>
              </label>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <div class="dataTables_paginate paging_simple_numbers float-end">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  @change="changePage"
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FormDeviceCamera
      :modal.sync="showFormModal"
      :isLoading.sync="isFormLoading"
      :initialData="formData"
      :deviceTypeOptions="deviceTypeOptions"
      @submit="handleFormSubmit"
    />

    <b-modal
      v-model="showDeleteModal"
      title="Confirm Delete"
      centered
      @ok="confirmDelete"
      @cancel="cancelDelete"
      ok-variant="danger"
      ok-title="Delete"
      cancel-title="Cancel"
    >
      <p>
        Are you sure you want to delete camera configuration for
        <strong>{{ deviceTypeLabel(deleteTarget && deleteTarget.device_code) }}</strong>?
      </p>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" :disabled="isDeleting" @click="cancel">
          Cancel
        </b-button>
        <b-button variant="danger" :disabled="isDeleting" @click="ok">
          <b-spinner v-if="isDeleting" small class="me-1"></b-spinner>
          {{ isDeleting ? 'Deleting...' : 'Delete' }}
        </b-button>
      </template>
    </b-modal>
  </Layout>
</template>
